import { useMemo } from 'react';

import { useFileEncryptionUpload } from '@/entities/file/hooks/use-file-encryption-upload';
import { languagesMap } from '@/shared/dictionaries/languages';
import { PrivateDocumentDto, useSJDatabase } from '@/shared/lib/sj-orm';
import { FileMetaInfoDto } from '@/shared/lib/sj-orm/models/file-meta-info.dto';
import { EntityPdfData, EntityPdfSection } from '@/shared/pdf-templates/EntityPDF/types';
import { dates } from '@/shared/utils/dates';

import { getPrivateDocSections } from '../helpers/getPrivateDocSections';
import { getSectionDataValue } from '../helpers/getSectionDataValue';
import { getSortedItems } from '../helpers/getSortedItems';
import { renderSignatories } from '../helpers/renderSignatories';
import { PrivateDocFields } from '../types';

import { useAttributeGroups } from './useAttributeGroups';
import { useI18nValues } from './useI18nValues';
import { usePrivateDocFormFields } from './usePrivateDocFormFields';

export const usePrivateDocumentPdfData = () => {
  const groups = useAttributeGroups();
  const sjDb = useSJDatabase();
  const { category } = useI18nValues();
  const { formData, formKeys, formatLabel } = usePrivateDocFormFields();
  const { findFileMetaByPath } = useFileEncryptionUpload();

  return useMemo(() => {
    const getEntityData = (privateDocument: PrivateDocumentDto): EntityPdfData => {
      const sortedItems = getSortedItems(formData, formKeys, privateDocument);
      const { general, details, languages, scanPaths } = getPrivateDocSections(sortedItems);
      const scans = scanPaths
        .map((filePath) => findFileMetaByPath(filePath))
        .filter(Boolean) as FileMetaInfoDto[];

      const generalItems = [
        ...general,
        {
          showIf: true,
          value: category(privateDocument.privateCategory),
          label: 'Category',
        },
      ] as PrivateDocFields;

      const initialSections = [generalItems, details];

      const generatedSections: EntityPdfSection[] = initialSections.map((sectionItems, index) => {
        return {
          title: groups[index].title,
          fields: Object.values(sectionItems).map((fieldDef) => ({
            label: formatLabel(fieldDef.label),
            value: getSectionDataValue(fieldDef),
          })),
        };
      });

      const documentLanguages: EntityPdfSection = {
        title: 'Document language(s)',
        fields: [{ value: languages.map((lang) => languagesMap.get(lang)?.i18nEn).join(', ') }],
      };

      const signatories: EntityPdfSection = {
        title: 'Signatory(es)',
        fields: [{ value: renderSignatories({ doc: privateDocument, sjDb }) }],
      };

      const documentScans: EntityPdfSection = {
        title: 'Document scans',
        itemGroups: [
          {
            title: 'Uploads',
            count: scans.length,
            items: scans.map((item) => ({
              fields: [
                {
                  label: 'Name',
                  value: item.name,
                },
                {
                  label: 'Upload date & time',
                  value: item.createdAt ? dates.formatDateTime(item.createdAt) : '-',
                },
                {
                  label: 'Comment',
                  value: item.comment ?? '-',
                },
              ],
            })),
          },
          {
            title: 'Email',
            count: 0,
            items: [
              // TODO: add emails here
              // it will be implemented after adding emails to doc dto
            ],
          },
        ],
      };

      const sections = [...generatedSections, documentLanguages, signatories, documentScans];

      return { sections };
    };

    return { getEntityData };
  }, [groups, formData, formKeys, formatLabel]);
};
